// Transforma as transações lidas do extrato em linhas prontas para
// contabil_lancamentos: chave de deduplicação, classificação pelas regras,
// partida dobrada e motivo de quem fica de fora. Puro (sem Supabase).

import { hashEstavel, normalizarTexto } from './comum';

export const codigoDaConta = (conta) => String(conta ?? '').trim().split(/\s+/)[0] || '';

// Ordena códigos contábeis como número por segmento (1.1.1.10 depois de 1.1.1.9).
export const compararCodigos = (a, b) =>
  String(a ?? '').localeCompare(String(b ?? ''), 'pt-BR', { numeric: true });

/**
 * Chave de deduplicação.
 *  - OFX com FITID: 'ofx:<acctid>:<fitid>' (o próprio banco garante a unicidade).
 *  - Demais: hash de conta|data|valor com sinal|descrição normalizada|ordinal,
 *    em que o ordinal é a ocorrência daquela mesma combinação dentro do arquivo
 *    (duas tarifas iguais no mesmo dia não colidem; reimportar o mesmo arquivo
 *    gera as mesmas chaves).
 */
export function gerarChaves(transacoes, { contaBanco, acctId }) {
  const ocorrencias = new Map();
  const codConta = codigoDaConta(contaBanco);
  return transacoes.map(t => {
    if (t.fitid) return `ofx:${acctId || codConta}:${t.fitid}`;
    const base = [codConta, t.data, Number(t.valor).toFixed(2), normalizarTexto(t.descricao)].join('|');
    const n = (ocorrencias.get(base) || 0) + 1;
    ocorrencias.set(base, n);
    return `ext:${hashEstavel(`${base}|${n}`)}`;
  });
}

/**
 * Regras na ordem em que são testadas: primeiro as gravadas no banco
 * (contabil_regras, respeitando o sentido), depois as do arquivo da empresa
 * como reserva (valem para os dois sentidos). Dentro de cada grupo, o padrão
 * mais longo vem antes (é o mais específico).
 */
export function montarRegras(regrasDb = [], regrasJson = []) {
  const doBanco = (regrasDb || [])
    .filter(r => r && r.padrao && r.conta_contabil)
    .map(r => ({ padrao: normalizarTexto(r.padrao), conta: r.conta_contabil, sentido: r.sentido || 'ambos', fonte: 'banco' }))
    .sort((a, b) => b.padrao.length - a.padrao.length);
  const doArquivo = (regrasJson || [])
    .filter(r => r && r.padrao && r.contaContabil)
    .map(r => ({ padrao: normalizarTexto(r.padrao), conta: r.contaContabil, sentido: 'ambos', fonte: 'arquivo' }));
  return [...doBanco, ...doArquivo].filter(r => r.padrao);
}

export function classificar(descricao, sentido, regras) {
  const d = normalizarTexto(descricao);
  return regras.find(r => (r.sentido === 'ambos' || r.sentido === sentido) && d.includes(r.padrao)) || null;
}

/**
 * @param {object} p
 * @param {Array}  p.transacoes        saída do parseCsv/parseOfx
 * @param {string} p.contaBanco        '1.1.1.03 — Banco Inter'
 * @param {string} p.acctId            conta do OFX, se houver
 * @param {Array}  p.regras            saída de montarRegras
 * @param {string} p.inicio            data de corte 'AAAA-MM-DD' (contabil_empresas)
 * @param {Set}    p.periodosFechados  'AAAA-MM'
 * @param {Set}    p.chavesExistentes  chave_dedup já gravadas
 */
export function prepararLinhas({ transacoes, contaBanco, acctId, regras, inicio, periodosFechados, chavesExistentes }) {
  const chaves = gerarChaves(transacoes, { contaBanco, acctId });
  const codBanco = codigoDaConta(contaBanco);
  const fechados = periodosFechados || new Set();
  const existentes = chavesExistentes || new Set();
  const vistas = new Set(); // FITID repetido no próprio arquivo (alguns bancos repetem)

  return transacoes.map((t, i) => {
    const sentido = t.valor > 0 ? 'entrada' : 'saida';
    const regra = classificar(t.descricao, sentido, regras);
    // Regra que aponta para o próprio banco não é contrapartida válida.
    const contrapartida = regra && codigoDaConta(regra.conta) !== codBanco ? regra.conta : null;
    const periodo = t.data.slice(0, 7);
    let situacao = 'nova';
    let motivo = '';
    if (inicio && t.data < inicio) {
      situacao = 'ignorada';
      motivo = `Anterior ao início da escrituração no sistema (${inicio.split('-').reverse().join('/')})`;
    } else if (fechados.has(periodo)) {
      situacao = 'ignorada';
      motivo = `Mês ${periodo.slice(5)}/${periodo.slice(0, 4)} fechado`;
    } else if (existentes.has(chaves[i])) {
      situacao = 'duplicada';
      motivo = 'Já importada anteriormente';
    } else if (vistas.has(chaves[i])) {
      situacao = 'duplicada';
      motivo = 'Repetida no próprio arquivo';
    }
    vistas.add(chaves[i]);
    return {
      ...t,
      chave: chaves[i],
      sentido,
      contrapartida,
      regraPadrao: contrapartida ? regra.padrao : null,
      contaDebito: sentido === 'entrada' ? contaBanco : contrapartida,
      contaCredito: sentido === 'entrada' ? contrapartida : contaBanco,
      valorAbs: Math.round(Math.abs(t.valor) * 100) / 100,
      situacao,
      motivo,
    };
  });
}

export function resumir(linhas) {
  const novas = linhas.filter(l => l.situacao === 'nova');
  const datas = linhas.map(l => l.data).sort();
  const motivos = {};
  linhas.filter(l => l.situacao === 'ignorada').forEach(l => { motivos[l.motivo] = (motivos[l.motivo] || 0) + 1; });
  return {
    total: linhas.length,
    periodoInicio: datas[0] || null,
    periodoFim: datas[datas.length - 1] || null,
    novas: novas.length,
    classificadas: novas.filter(l => l.contrapartida).length,
    aClassificar: novas.filter(l => !l.contrapartida).length,
    duplicadas: linhas.filter(l => l.situacao === 'duplicada').length,
    ignoradas: linhas.filter(l => l.situacao === 'ignorada').length,
    motivosIgnoradas: motivos,
  };
}

// Linhas para o upsert em contabil_lancamentos.
export function paraRegistros(linhas, { empresaSlug, extratoId, centroCusto }) {
  return linhas
    .filter(l => l.situacao === 'nova')
    .map(l => ({
      empresa_slug: empresaSlug,
      data: l.data,
      historico: l.descricao,
      conta_debito: l.contaDebito || null,
      conta_credito: l.contaCredito || null,
      valor: l.valorAbs,
      documento: l.documento || null,
      centro_custo: centroCusto || null,
      origem: 'extrato',
      extrato_id: extratoId,
      chave_dedup: l.chave,
    }));
}
